// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Info, Lock } from "lucide-react";
import { Fragment, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { buildUserEditBaseBody, useUpdateUserMutation, useUserByPublicIdQuery } from "@/api/hooks/use-users";
import { PageFooter } from "@/components/layout/page-footer";
import { SaveDiscardPane } from "@/components/shared/save-discard-pane";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  MANAGED_BO_PERMISSIONS as MANAGED_PERMISSIONS,
  BO_NODE_DESCRIPTIONS as NODE_DESCRIPTIONS,
  BO_PERMISSION_SECTIONS as PERMISSION_SECTIONS,
  boPermissionSetsEqual as permissionSetsEqual,
} from "@/features/iam/permission-trees";
import { permissionMatches } from "@/lib/auth/page-permissions";
import { useDebugMode } from "@/lib/debug-mode";
import { countSetChanges } from "@/lib/dirty-fields";
import { cn } from "@/lib/utils";

export interface UserBoPermissionsTabHandle {
  reset: () => void;
}

interface UserBoPermissionsTabProps {
  publicId: string;
  onDirtyChange?: (dirty: boolean) => void;
  ref?: React.Ref<UserBoPermissionsTabHandle>;
}

export function UserBoPermissionsTab({ publicId, onDirtyChange, ref }: UserBoPermissionsTabProps) {
  const { t } = useTranslation("back-office");
  const { debugMode } = useDebugMode();
  const { data } = useUserByPublicIdQuery(publicId);
  const updateUser = useUpdateUserMutation(publicId);

  // Hide rows/sections for pages that are only reachable in debug mode.
  const sections = useMemo(
    () =>
      PERMISSION_SECTIONS.filter((section) => debugMode || !section.debugOnly).map((section) => ({
        ...section,
        nodes: section.nodes.filter((node) => debugMode || !node.debugOnly),
      })),
    [debugMode],
  );

  // All permissions assigned directly to the user (across every domain) — used on save to preserve
  // the slices this tab doesn't manage (product/app permissions, unknown grants).
  const allDirectPermissions = useMemo(
    () =>
      (data?.permissions ?? []).filter((source) => source.groupName === null).flatMap((source) => source.permissions),
    [data],
  );
  // Permissions assigned directly to the user (the editable set) — may include wildcards.
  const directPermissions = useMemo(
    () => new Set(allDirectPermissions.filter((permission) => permission.startsWith("backoffice:"))),
    [allDirectPermissions],
  );
  // Permissions inherited from groups (read-only here) → group they come from.
  const inheritedList = useMemo(() => {
    const list: { permission: string; groupName: string }[] = [];
    for (const source of data?.permissions ?? []) {
      if (source.groupName === null) continue;
      for (const permission of source.permissions) {
        if (permission.startsWith("backoffice:")) list.push({ permission, groupName: source.groupName });
      }
    }
    return list;
  }, [data]);
  // Distinct groups this user inherits backoffice permissions from.
  const inheritedGroups = useMemo(
    () => [...new Set(inheritedList.map((entry) => entry.groupName))].sort((a, b) => a.localeCompare(b)),
    [inheritedList],
  );

  // Baseline the dirty check compares against. It tracks the persisted state, but we advance it on a
  // successful save so the form clears immediately — without waiting for the refetched data.
  const [baseline, setBaseline] = useState<Set<string>>(() => new Set(directPermissions));
  const [enabled, setEnabled] = useState<Set<string>>(() => new Set(directPermissions));

  useEffect(
    function resetWhenDataLoads() {
      setEnabled(new Set(directPermissions));
      setBaseline(new Set(directPermissions));
    },
    [directPermissions],
  );

  const isDirty = !permissionSetsEqual(enabled, baseline);

  useImperativeHandle(ref, () => ({ reset: () => setEnabled(new Set(baseline)) }));

  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(
    function notifyParentOfDirtyState() {
      onDirtyChangeRef.current?.(isDirty);
    },
    [isDirty],
  );

  // The group (if any) whose grant satisfies `required` — exact or via wildcard.
  function inheritedFrom(required: string): string | undefined {
    return inheritedList.find((entry) => permissionMatches(entry.permission, required))?.groupName;
  }
  // A broader directly-granted permission that already covers `required` (e.g. a section wildcard).
  function coveredByOther(required: string): string | undefined {
    for (const granted of enabled) {
      if (granted !== required && permissionMatches(granted, required)) return granted;
    }
    return undefined;
  }

  // Edit implies View: enabling an edit grant also grants its view; revoking the view also revokes its edit.
  function toggleView(view: string, edit?: string) {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(view)) {
        next.delete(view);
        if (edit) next.delete(edit);
      } else {
        next.add(view);
      }
      return next;
    });
  }
  function toggleEdit(view: string, edit: string) {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(edit)) {
        next.delete(edit);
      } else {
        next.add(edit);
        next.add(view);
      }
      return next;
    });
  }

  function renderSwitch(permission: string | undefined, onToggle: () => void) {
    if (!permission) return <span className="text-muted-foreground">—</span>;

    const inheritedGroup = inheritedFrom(permission);
    if (inheritedGroup) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <Switch checked disabled aria-readonly className="pointer-events-none opacity-60" />
              <Lock className="h-3 w-3" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            {t("users.permissions.inheritedFromGroup", { group: inheritedGroup })}
          </TooltipContent>
        </Tooltip>
      );
    }

    const covered = coveredByOther(permission);
    if (covered) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center text-muted-foreground">
              <Switch checked disabled aria-readonly className="pointer-events-none opacity-60" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="font-mono text-xs">
            {t("users.permissions.grantedVia", { permission: covered })}
          </TooltipContent>
        </Tooltip>
      );
    }

    return <Switch checked={enabled.has(permission)} onCheckedChange={onToggle} />;
  }

  async function onSave() {
    if (!data) return;
    // Resend the full direct-permission set: the slices this tab doesn't manage (preserved as-is)
    // plus the switches enabled here.
    const preserved = allDirectPermissions.filter((permission) => !MANAGED_PERMISSIONS.has(permission));
    const permissions = [...new Set([...preserved, ...enabled])];
    try {
      await updateUser.mutateAsync({ ...buildUserEditBaseBody(data), permissions });
      // Clear the dirty state right away, ahead of the refetch reconciling `directPermissions`.
      setBaseline(new Set(enabled));
      toast.success(t("users.notifications.updated", { name: data.user.fullName || data.user.email }));
    } catch {
      // Error toast handled by API middleware
    }
  }

  // A single grid row: label cell + View cell + Edit cell. Borders come from the grid container.
  function gridRow(
    key: string,
    label: React.ReactNode,
    view: React.ReactNode,
    edit: React.ReactNode,
    variant?: "header" | "section" | "node",
    separator?: boolean,
    description?: string,
  ) {
    const labelClass =
      variant === "header" ? "text-xs text-muted-foreground" : variant === "section" ? "font-medium" : undefined;
    // The label column stays open; a section divider is drawn as `border-b` on the row *above* a
    // section (`separator`) so it lines up exactly with the View/Edit columns' bottom borders.
    return (
      <Fragment key={key}>
        <div className={cn("px-3 py-2", variant === "node" && "pl-6", separator && "border-b")}>
          {description ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    "cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-4",
                    labelClass,
                  )}
                >
                  {label}
                </span>
              </TooltipTrigger>
              <TooltipContent side="right">{description}</TooltipContent>
            </Tooltip>
          ) : (
            <span className={labelClass}>{label}</span>
          )}
        </div>
        <div className="min-h-9 justify-center py-1.5">{view}</div>
        <div className="min-h-9 justify-center py-1.5">{edit}</div>
      </Fragment>
    );
  }

  const dash = <span className="text-muted-foreground">—</span>;

  return (
    <div className="max-w-2xl space-y-6 p-6">
      {inheritedGroups.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {t("users.permissions.inheritBanner.prefix")}{" "}
            {inheritedGroups.map((group, index) => (
              <span key={group}>
                {index > 0 && ", "}
                <span className="font-medium text-foreground">{group}</span>
              </span>
            ))}
            . {t("users.permissions.inheritBanner.suffix")}
          </span>
        </div>
      )}

      <div
        className={cn(
          "grid grid-cols-[1fr_3.5rem_3.5rem] text-sm",
          "[&>*]:flex [&>*]:items-center",
          // Borders only around the View/Edit columns — the label column stays open.
          "[&>*:nth-child(3n+2)]:border-l [&>*:nth-child(3n+2)]:border-b",
          "[&>*:nth-child(3n+3)]:border-x [&>*:nth-child(3n+3)]:border-b",
          "[&>*:nth-child(2)]:border-t [&>*:nth-child(3)]:border-t",
        )}
      >
        {gridRow("__header", null, t("users.permissions.view"), t("users.permissions.edit"), "header")}

        {/* Full access — the top-level `backoffice:*` / `backoffice:*:edit` wildcards */}
        {gridRow(
          "__full-access",
          t("users.permissions.fullAccess"),
          renderSwitch("backoffice:*", () => toggleView("backoffice:*", "backoffice:*:edit")),
          renderSwitch("backoffice:*:edit", () => toggleEdit("backoffice:*", "backoffice:*:edit")),
          "section",
          true,
          t("users.permissions.everyBackofficePermission"),
        )}

        {sections.map((section, sectionIndex) => {
          const viewWildcard = `${section.prefix}:*`;
          const editWildcard = `${section.prefix}:*:edit`;
          const hasEdit = section.nodes.some((node) => node.edit);
          const isLastSection = sectionIndex === sections.length - 1;
          return (
            <Fragment key={section.prefix}>
              {gridRow(
                `${section.prefix}__header`,
                t(section.label),
                renderSwitch(viewWildcard, () => toggleView(viewWildcard, editWildcard)),
                hasEdit ? renderSwitch(editWildcard, () => toggleEdit(viewWildcard, editWildcard)) : dash,
                "section",
                false,
                t("users.permissions.allPermissions", { section: t(section.label) }),
              )}
              {section.nodes.map((node, nodeIndex) =>
                gridRow(
                  node.view,
                  t(node.label),
                  renderSwitch(node.view, () => toggleView(node.view, node.edit)),
                  node.edit ? renderSwitch(node.edit, () => toggleEdit(node.view, node.edit as string)) : dash,
                  "node",
                  // Divider below the last node of every section except the last — lines up the
                  // open label column with the next section's top border.
                  !isLastSection && nodeIndex === section.nodes.length - 1,
                  NODE_DESCRIPTIONS[node.view],
                ),
              )}
            </Fragment>
          );
        })}
      </div>

      {isDirty && (
        <PageFooter>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSave();
            }}
          >
            <SaveDiscardPane
              docked
              onDiscard={() => setEnabled(new Set(baseline))}
              saving={updateUser.isPending}
              changeCount={countSetChanges(enabled, baseline)}
            />
          </form>
        </PageFooter>
      )}
    </div>
  );
}
